import { Task } from 'redux-saga';
import { cancel, put, select, takeLatest, takeLeading } from 'redux-saga/effects';

import {
  getUserSingleSiteSchedule,
  SiteScheduleInfo,
  UserSingleSiteSchedulePayload
} from '../../../utils/siteSchedule';
import { ExtendedSiteScheduleInfo } from '../../../utils/scheduleMapper';
import { RootState } from '../../reducers';
import { QuestionnaireData, QuestionnaireIndexes } from '../questionnaires/type';
import * as actions from './siteSchedule.action';
import * as types from './siteSchedule.actionTypes';

function* fetchUserSingleSiteSchedule(action: ReturnType<typeof actions.fetchSingleSiteScheduleRequest>): Generator {
  const { siteKey, startDate, endDate } = action.payload;
  yield put(actions.setIsLoading(siteKey, true));
  try {
    const payload: UserSingleSiteSchedulePayload = {
      siteID: siteKey,
      startDate,
      endDate
    };
    // @ts-ignore
    const siteScheduleInfo: ExtendedSiteScheduleInfo[] = yield getUserSingleSiteSchedule(payload);

    // Enrich questionnaireDetails from Redux questionnaire state
    // (REST API returns only questionnaireID/Name; full content lives in state.questionnaires.data)
    const questionnairesData: QuestionnaireData = yield select((state: RootState) => state.questionnaires.data);
    // state.questionnaireIndex is NOT persisted; use state.questionnaires.index as fallback (persisted)
    const questionnaireIndexData: QuestionnaireIndexes = yield select(
      (state: RootState) => state.questionnaireIndex.questionnaireIndex ?? state.questionnaires.index
    );

    const enriched = siteScheduleInfo.map(schedule => {
      if (schedule.questionnaireDetails) return schedule;
      const qID = schedule.questionnaireID;
      if (!qID || !questionnairesData?.[qID] || !questionnaireIndexData?.[qID]) return schedule;
      return {
        ...schedule,
        questionnaireDetails: {
          key: schedule.key,
          value: {
            ...questionnaireIndexData[qID],
            questionnaire: questionnairesData[qID]
          }
        }
      };
    });

    yield put(actions.setSingleSiteScheduleData(siteKey, enriched as SiteScheduleInfo[]));
    yield put(actions.setIsLoading(siteKey, false));
  } catch (error) {
    yield put(actions.setHasError(siteKey, true));
  }
}

function* cancelWorkerSaga(task: Task) {
  yield cancel(task);
}

export default function* siteScheduleSaga() {
  const fetchSingleSiteSaga: Task = yield takeLatest(
    types.FETCH_SINGLE_SITE_SCHEDULE_REQUEST,
    fetchUserSingleSiteSchedule
  );
  yield takeLeading(types.CANCEL, cancelWorkerSaga, fetchSingleSiteSaga);
}
